import { useState, useEffect } from "react";
import ImageUpload from "../../components/ImageUpload/ImageUpload";
import { IBlog } from "../../types";
import { BLOG_CATEGORIES } from "../../../utils/constants";
import { useAuth } from "../../context/AuthContext";

interface BlogFormProps {
  blog: IBlog | null;
  onSubmit: (formData: FormData) => void;
  onCancel: () => void;
}

const BlogForm = ({ blog, onSubmit, onCancel }: BlogFormProps) => {
  const { admin } = useAuth();
  const [formData, setFormData] = useState({
    title: "",
    excerpt: "",
    content: "",
    category: "",
    author: admin?.id || "",
    tags: "",
    featured: false,
    published: false,
    source: "local" as "local" | "medium",
    mediaLink: "",
    metaDescription: "",
    seoKeywords: "",
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (blog) {
      setFormData({
        title: blog.title,
        excerpt: blog.excerpt,
        content: blog.content,
        category: blog.category,
        author:
          typeof blog.author === "string" ? blog.author : blog.author._id || "",
        tags: blog.tags?.join(", ") || "",
        featured: blog.featured || false,
        published: blog.published || false,
        source: blog.source || "local",
        mediaLink: blog.mediaLink || "",
        metaDescription: blog.metaDescription || "",
        seoKeywords: blog.seoKeywords || "",
      });
      setImagePreview(blog.featuredImage);
    }
  }, [blog]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    const data = new FormData();
    data.append("title", formData.title);
    data.append("excerpt", formData.excerpt);
    data.append("content", formData.content);
    data.append("category", formData.category);
    data.append("author", formData.author);
    data.append("featured", String(formData.featured));
    data.append("published", String(formData.published));
    data.append("source", formData.source);

    if (formData.tags) {
      const tagsArray = formData.tags.split(",").map((tag) => tag.trim());
      data.append("tags", JSON.stringify(tagsArray));
    }

    if (formData.mediaLink) {
      data.append("mediaLink", formData.mediaLink);
    }

    if (formData.metaDescription) {
      data.append("metaDescription", formData.metaDescription);
    }

    if (formData.seoKeywords) {
      data.append("seoKeywords", formData.seoKeywords);
    }

    if (imageFile) {
      data.append("featuredImage", imageFile);
    }

    await onSubmit(data);
    setSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit} className="admin-form">
      <ImageUpload
        value={imagePreview}
        onChange={setImageFile}
        label="Featured Image"
      />

      <div className="form-group">
        <label>Blog Title *</label>
        <input
          type="text"
          name="title"
          value={formData.title}
          onChange={handleChange}
          required
          placeholder="Enter blog title"
        />
      </div>

      <div className="form-group">
        <label>Excerpt *</label>
        <textarea
          name="excerpt"
          value={formData.excerpt}
          onChange={handleChange}
          required
          rows={3}
          placeholder="Enter a short excerpt (10-500 characters)"
        />
      </div>

      <div className="form-group">
        <label>Content *</label>
        <textarea
          name="content"
          value={formData.content}
          onChange={handleChange}
          required
          rows={10}
          placeholder="Enter blog content (minimum 50 characters)"
        />
      </div>

      <div className="form-group">
        <label>Category *</label>
        <select
          name="category"
          value={formData.category}
          onChange={handleChange}
          required
        >
          <option value="">Select category</option>
          {BLOG_CATEGORIES.map((cat) => (
            <option key={cat} value={cat}>
              {cat}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label>Tags (comma separated)</label>
        <input
          type="text"
          name="tags"
          value={formData.tags}
          onChange={handleChange}
          placeholder="e.g., Technology, Innovation, AI"
        />
      </div>

      <div className="form-group">
        <label>Source</label>
        <select name="source" value={formData.source} onChange={handleChange}>
          <option value="local">Local</option>
          <option value="medium">Medium</option>
        </select>
      </div>

      <div className="form-group">
        <label>Media Link (Optional)</label>
        <input
          type="url"
          name="mediaLink"
          value={formData.mediaLink}
          onChange={handleChange}
          placeholder="https://medium.com/your-post"
        />
      </div>

      <div className="form-group">
        <label>Meta Description (SEO)</label>
        <textarea
          name="metaDescription"
          value={formData.metaDescription}
          onChange={handleChange}
          rows={2}
          placeholder="Enter meta description for SEO (max 160 characters)"
          maxLength={160}
        />
      </div>

      <div className="form-group">
        <label>SEO Keywords</label>
        <input
          type="text"
          name="seoKeywords"
          value={formData.seoKeywords}
          onChange={handleChange}
          placeholder="Enter keywords for SEO"
        />
      </div>

      <div className="form-group-inline">
        <label className="checkbox-label">
          <input
            type="checkbox"
            name="featured"
            checked={formData.featured}
            onChange={handleChange}
          />
          <span>Featured Post</span>
        </label>

        <label className="checkbox-label">
          <input
            type="checkbox"
            name="published"
            checked={formData.published}
            onChange={handleChange}
          />
          <span>Published</span>
        </label>
      </div>

      <div className="form-actions">
        <button
          type="button"
          className="btn-secondary"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </button>
        <button type="submit" className="btn-primary" disabled={submitting}>
          {submitting ? "Saving..." : blog ? "Update Post" : "Create Post"}
        </button>
      </div>
    </form>
  );
};

export default BlogForm;
